import React, { useEffect, useState } from "react";
import {
  Anchor,
  Box,
  Button,
  Center,
  CopyButton,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { IconCheck, IconCopy, IconFileSearch } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { TestInvoice } from "../helpers/lnurlPayTest";

const useSecondsLeft = (expiresAt: number | null): number | null => {
  const left = () =>
    expiresAt === null ? null : Math.max(0, expiresAt - Math.floor(Date.now() / 1000));
  const [secondsLeft, setSecondsLeft] = useState(left);
  useEffect(() => {
    setSecondsLeft(left());
    if (expiresAt === null) return;
    const timer = setInterval(() => setSecondsLeft(left()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  return secondsLeft;
};

const countdownText = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

/** The test invoice: QR, copy, open-in-wallet, decode link and an expiry countdown. */
const TestInvoiceDisplay: React.FC<{ invoice: TestInvoice }> = ({ invoice }) => {
  const secondsLeft = useSecondsLeft(invoice.expiresAt);
  const expired = secondsLeft === 0;

  return (
    <Stack gap="sm">
      <Text size="sm" style={{ color: "#fdba74" }}>
        This is a real invoice. Paying it sends real sats to the owner of this
        address.
      </Text>
      <Center style={{ opacity: expired ? 0.3 : 1 }}>
        <Box style={{ width: "100%", maxWidth: 260 }}>
          <QRCodeSVG
            value={invoice.pr.toUpperCase()}
            title="Test invoice"
            size={260}
            bgColor="#ffffff"
            fgColor="#000000"
            level="L"
            marginSize={4}
            style={{ width: "100%", height: "auto", borderRadius: 6 }}
          />
        </Box>
      </Center>
      <Text ta="center" size="sm" c={expired ? "red.4" : "dimmed"}>
        {invoice.amountSats !== null &&
          `${invoice.amountSats.toLocaleString()} sats · `}
        {secondsLeft === null
          ? "expiry unknown"
          : expired
            ? "expired"
            : `expires in ${countdownText(secondsLeft)}`}
      </Text>
      <Text
        size="xs"
        c="dimmed"
        style={{ fontFamily: "monospace", wordBreak: "break-all", opacity: 0.7 }}
      >
        {invoice.pr}
      </Text>
      <Group justify="center" gap="xs">
        <CopyButton value={invoice.pr}>
          {({ copied, copy }) => (
            <Button
              size="xs"
              variant={copied ? "filled" : "outline"}
              color={copied ? "teal" : "gray"}
              leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              onClick={copy}
            >
              {copied ? "Copied" : "Copy invoice"}
            </Button>
          )}
        </CopyButton>
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          component="a"
          href={`/decode_invoice?invoice=${encodeURIComponent(invoice.pr)}`}
          target="_blank"
          leftSection={<IconFileSearch size={14} />}
        >
          Decode it
        </Button>
      </Group>
      <Text size="xs" c="dimmed" ta="center">
        You don't need to pay it: the checks above are already done.{" "}
        <Anchor href="/create-account" size="xs" c="orange.4">
          Want your own address?
        </Anchor>
      </Text>
    </Stack>
  );
};

export default TestInvoiceDisplay;
